/* STERK STORY — theme scripts (no dependencies) */
// UI texts come from the locale files (window.STERK_T, set in layout/theme.liquid); [name] placeholders are filled here.
const tr = (key, vars = {}) => String((window.STERK_T || {})[key] ?? key).replace(/\[(\w+)\]/g, (_, name) => (vars[name] ?? ''));

(() => {
  const cartUrl = (path) => (window.Shopify?.routes?.root || '/') + path;

  // ---------- Header: solid after scrolling, hides while scrolling down ----------
  const header = document.querySelector('[data-header]');
  if (header) {
    const shell = header.closest('.section-header') || header;
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      if (header.hasAttribute('data-hide-on-scroll')) {
        const goingDown = y > lastY && y > 320;
        shell.classList.toggle('is-hidden', goingDown && !document.querySelector('dialog[open]'));
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  // ---------- Hero background video ----------
  document.querySelectorAll('.hero__video').forEach((video) => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const toggle = video.closest('.hero')?.querySelector('[data-hero-video-toggle]');
    // Paused by the visitor (button) or by the system setting; the observer never overrides it
    let stopped = reduce.matches;
    const render = () => {
      if (!toggle) return;
      toggle.classList.toggle('is-paused', stopped);
      toggle.querySelector('.hero__video-label').textContent = stopped ? tr('video_play') : tr('video_pause');
    };
    const sync = () => { if (stopped) video.pause(); else video.play?.().catch(() => {}); render(); };
    sync();
    reduce.addEventListener?.('change', () => { stopped = reduce.matches; sync(); });
    toggle?.addEventListener('click', () => { stopped = !stopped; sync(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        if (stopped) return;
        if (entry.isIntersecting) video.play?.().catch(() => {}); else video.pause();
      }).observe(video);
    }
  });

  // ---------- Order tracking (forwards to the Track123 app proxy) ----------
  document.querySelectorAll('[data-tracking]').forEach((panel) => {
    const base = panel.dataset.trackingUrl || '/apps/track123';
    const tabs = panel.querySelectorAll('[data-tracking-tab]');
    const forms = panel.querySelectorAll('[data-tracking-form]');
    const select = (name) => {
      tabs.forEach((t) => {
        const on = t.dataset.trackingTab === name;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
      });
      forms.forEach((f) => { f.hidden = f.dataset.trackingForm !== name; });
    };
    tabs.forEach((t) => t.addEventListener('click', () => select(t.dataset.trackingTab)));
    // Track123 expects the e-mail base64-encoded (UTF-8) in "id"
    const b64 = (s) => btoa(String.fromCharCode(...new TextEncoder().encode(s)));
    forms.forEach((form) => form.addEventListener('submit', (e) => {
      e.preventDefault();
      const error = form.querySelector('.tracking__error');
      const valid = [...form.querySelectorAll('input')].every((i) => i.value.trim() && i.checkValidity());
      error.hidden = valid;
      if (!valid) return;
      const v = (n) => form.elements[n].value.trim();
      const query = form.dataset.trackingForm === 'number'
        ? 'nums=' + encodeURIComponent(v('nums'))
        : 'order=' + encodeURIComponent(v('order')) + '&id=' + encodeURIComponent(b64(v('email')));
      // Forward only if the tracking app answers; otherwise point to the customer account
      const fallback = panel.querySelector('[data-tracking-fallback]');
      fetch(base, { method: 'HEAD', cache: 'no-store' })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); window.location.href = base + '?' + query; })
        .catch(() => { if (fallback) fallback.hidden = false; });
    }));
  });

  // ---------- Gift surcharge: exactly one while a gift item is in the cart ----------
  // Returns the fixing request, or null when the cart is already consistent.
  const postCart = (path, body) => fetch(cartUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const giftFix = (el) => {
    if (!el) return null;
    const { giftItems, feeKey, feeQty, feeVariant } = el.dataset;
    if (Number(giftItems) > 0 && !feeKey && feeVariant) return postCart('cart/add.js', { items: [{ id: Number(feeVariant), quantity: 1 }] });
    if (Number(giftItems) === 0 && feeKey) return postCart('cart/change.js', { id: feeKey, quantity: 0 });
    if (feeKey && Number(feeQty) !== 1) return postCart('cart/change.js', { id: feeKey, quantity: 1 });
    return null;
  };

  // ---------- Cart drawer (not rendered on /cart, so everything falls back to the cart page) ----------
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const setCartCount = (count) => {
    document.querySelectorAll('.site-header__cart').forEach((link) => {
      link.setAttribute('aria-label', tr('cart_count', { count }));
      let badge = link.querySelector('.site-header__count');
      if (count > 0 && !badge) {
        badge = document.createElement('span');
        badge.className = 'site-header__count';
        link.append(badge);
      }
      if (badge) {
        if (count > 0) badge.textContent = count; else badge.remove();
      }
    });
  };
  const refreshCart = async (retry = true) => {
    const response = await fetch(cartUrl('?sections=cart-drawer'), { cache: 'no-store' });
    const html = (await response.json())['cart-drawer'];
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-body]');
    if (!fresh) return;
    cartDrawer.querySelector('[data-cart-drawer-body]').replaceWith(fresh);
    setCartCount(Number(fresh.dataset.count));
    const fix = retry && giftFix(fresh.querySelector('[data-gift-cart-drawer]'));
    if (fix) { await fix; await refreshCart(false); }
  };
  const openCart = async () => {
    if (!cartDrawer) { window.location.href = cartUrl('cart'); return; }
    if (!cartDrawer.open) cartDrawer.showModal();
    try { await refreshCart(); } catch (e) { /* the server-rendered contents stay */ }
  };
  cartDrawer?.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    remove.disabled = true;
    try {
      await postCart('cart/change.js', { id: remove.dataset.cartRemove, quantity: 0 });
      await refreshCart();
    } catch (e) {
      window.location.href = cartUrl('cart');
    }
  });

  // ---------- Drawers (native <dialog>) ----------
  document.addEventListener('click', (event) => {
    if (cartDrawer && event.target.closest('[data-cart-open]')) {
      event.preventDefault();
      openCart();
      return;
    }
    // v3.33 Live chat: opens the Shopify Inbox window (show(); fallback: its own launcher button)
    const chatOpener = event.target.closest('[data-chat-open]');
    if (chatOpener) {
      const chat = document.querySelector('shopify-chat');
      if (chat) {
        event.preventDefault();
        chatOpener.closest('dialog[open]')?.close();
        if (typeof chat.show === 'function') chat.show();
        else chat.shadowRoot?.querySelector('button.activator')?.click();
      }
      return;
    }
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.drawerOpen);
      if (!target) return; // a link opener then simply follows its href
      event.preventDefault();
      opener.closest('dialog[open]')?.close(); // e.g. "Anmelden" inside the menu drawer
      // Search panel: sits exactly over the header row (below the announcement bar while it is in view)
      if (target.id === 'SearchPanel') {
        const header = document.querySelector('[data-header]');
        target.style.top = header ? Math.max(0, Math.round(header.getBoundingClientRect().top)) + 'px' : '0px';
      }
      target.showModal();
      // Account portal: e-mail field ready to type on desktop (no keyboard pop-up on touch devices)
      const email = target.querySelector('[data-account-form] input[type="email"]');
      if (email && window.matchMedia('(hover: hover) and (pointer: fine)').matches) email.focus();
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      event.target.closest('dialog')?.close();
      return;
    }
    // Click on the backdrop closes the drawer
    if (event.target.matches('dialog.drawer, dialog.search-panel')) event.target.close();
  });

  // ---------- Account portal: e-mail first ----------
  // The address goes to Shopify's login as "login_hint" (pre-filled there; the code is sent by Shopify).
  document.querySelectorAll('[data-account-form]').forEach((form) => {
    const input = form.querySelector('input[type="email"]');
    const error = form.querySelector('.account-portal__error');
    const setError = (on) => {
      error.hidden = !on;
      input.setAttribute('aria-invalid', on ? 'true' : 'false');
    };
    form.addEventListener('submit', (event) => {
      input.value = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(input.value)) {
        event.preventDefault();
        setError(true);
        input.focus();
        return;
      }
      setError(false);
      // Step 2 (same panel): profile fields + AGB, unless "direkt weiter" was chosen there
      const step2 = form.parentElement.querySelector('[data-portal-register]');
      if (step2 && form.dataset.direct !== '1') {
        event.preventDefault();
        step2.querySelector('[data-portal-email]').textContent = input.value;
        if (step2.hidden) {
          step2.hidden = false;
          step2.dataset.shownAt = String(Date.now());
          form.classList.add('is-step2'); // hides only the WEITER button; the address stays editable
          form.parentElement.querySelector('[data-portal-extras]')?.setAttribute('hidden', '');
          step2.querySelector('select, input')?.focus();
        }
        return;
      }
      form.querySelector('[type="submit"]')?.setAttribute('aria-busy', 'true');
    });
    input.addEventListener('input', () => { if (!error.hidden) setError(false); });
  });

  // ---------- Favoriten (wishlist) ----------
  // Product handles, newest first, kept in this browser (localStorage). Saving is triggered by the
  // visitor's own click, so no consent is needed (§ 25 Abs. 2 Nr. 2 TDDDG).
  // Signed-in customers: the list is also stored in their customer account (metafield sterk.favoriten)
  // through the App Proxy /apps/sterk/favoriten (Cloudflare Worker "sterk-favoriten"), so it follows
  // them to every device. Guests keep the browser-only list; it is merged into the account on login.
  const FAV_KEY = 'sterk:favoriten';
  const FAV_OWNER_KEY = 'sterk:favoriten:konto'; // customer id the local list belongs to ('' = guest)
  const FAV_PENDING_KEY = 'sterk:favoriten:offen'; // changes not yet confirmed by the account: {add, remove}
  const FAV_MAX = 60;
  const FAV_HANDLE = /^[\p{L}\p{N}][\p{L}\p{N}_-]{0,254}$/u;
  const favStore = (() => {
    try {
      const probe = '__sterk_probe';
      window.localStorage.setItem(probe, probe);
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch (e) {
      return null; // blocked storage / some private modes: the hearts stay hidden
    }
  })();
  const readFavs = () => {
    if (!favStore) return [];
    try {
      const list = JSON.parse(favStore.getItem(FAV_KEY) || '[]');
      return Array.isArray(list) ? [...new Set(list.filter((h) => typeof h === 'string' && FAV_HANDLE.test(h)))] : [];
    } catch (e) {
      return [];
    }
  };
  const writeFavs = (list) => {
    try {
      favStore.setItem(FAV_KEY, JSON.stringify(list.slice(0, FAV_MAX)));
      return true;
    } catch (e) {
      return false;
    }
  };
  const favPage = document.querySelector('[data-favorites]');
  const favUrl = () => document.querySelector('[data-fav-link]')?.getAttribute('href') || cartUrl('pages/favoriten');

  // --- Account sync ---
  const favClean = (list) => (Array.isArray(list) ? [...new Set(list.filter((h) => typeof h === 'string' && FAV_HANDLE.test(h)))] : []);
  // Rendered by layout/theme.liquid only for a signed-in customer (pages for customers are never shared-cached)
  const favAccount = (() => {
    try {
      const el = document.getElementById('FavAccount');
      if (!el) return null;
      const data = JSON.parse(el.textContent);
      return data && data.id ? { id: String(data.id), list: favClean(data.list) } : null;
    } catch (e) {
      return null;
    }
  })();
  const favGet = (key, fallback) => {
    try {
      const value = favStore.getItem(key);
      return value === null ? fallback : JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  };
  const favSet = (key, value) => {
    try {
      if (value === null) favStore.removeItem(key);
      else favStore.setItem(key, JSON.stringify(value));
    } catch (e) { /* storage full or blocked: the account copy still works */ }
  };
  const readPending = () => {
    const p = favGet(FAV_PENDING_KEY, null) || {};
    return { add: favClean(p.add), remove: favClean(p.remove) };
  };
  const writePending = (p) => favSet(FAV_PENDING_KEY, p.add.length || p.remove.length ? p : null);
  // Same rules as the Worker: newest additions first, removal wins
  const applyFavOps = (list, add, remove) => {
    const removed = new Set(remove);
    return [...new Set([...add.filter((h) => !removed.has(h)), ...list])].filter((h) => !removed.has(h)).slice(0, FAV_MAX);
  };
  const queueFav = (add, remove) => {
    if (!favAccount) return;
    const p = readPending();
    const addSet = new Set(add);
    const removeSet = new Set(remove);
    p.add = [...add, ...p.add.filter((h) => !addSet.has(h) && !removeSet.has(h))];
    p.remove = [...remove, ...p.remove.filter((h) => !addSet.has(h) && !removeSet.has(h))];
    writePending(p);
    scheduleFavFlush();
  };

  let favFlushTimer = 0;
  let favFlushing = false;
  let favFlushAgain = false;
  const scheduleFavFlush = (delay = 600) => {
    clearTimeout(favFlushTimer);
    favFlushTimer = setTimeout(flushFavs, delay);
  };
  const flushFavs = async () => {
    if (!favAccount) return;
    if (favFlushing) { favFlushAgain = true; return; }
    const sent = readPending();
    favFlushing = true;
    try {
      const response = await fetch(cartUrl('apps/sterk/favoriten'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(sent),
        credentials: 'same-origin',
      });
      if (!response.ok) return; // keep the changes; next page view retries
      const server = favClean((await response.json()).list);
      // Drop what the account confirmed; anything clicked meanwhile stays pending
      const now = readPending();
      const sentAdd = new Set(sent.add);
      const sentRemove = new Set(sent.remove);
      const rest = { add: now.add.filter((h) => !sentAdd.has(h)), remove: now.remove.filter((h) => !sentRemove.has(h)) };
      writePending(rest);
      const before = JSON.stringify(readFavs());
      const next = applyFavOps(server, rest.add, rest.remove);
      if (JSON.stringify(next) !== before) {
        writeFavs(next);
        syncFavButtons();
        renderFavPage();
      }
      if (rest.add.length || rest.remove.length) favFlushAgain = true;
    } catch (e) {
      /* offline: keep the changes; next page view retries */
    } finally {
      favFlushing = false;
      if (favFlushAgain) { favFlushAgain = false; scheduleFavFlush(); }
    }
  };

  // Decide once per page view which list this browser shows
  if (favStore) {
    const owner = favGet(FAV_OWNER_KEY, '');
    if (favAccount) {
      let pending = readPending();
      if (owner !== favAccount.id) {
        // Guest list -> merge into the account. A list of ANOTHER customer is discarded (shared device).
        pending = owner ? { add: [], remove: [] } : { add: readFavs(), remove: [] };
        favSet(FAV_OWNER_KEY, favAccount.id);
      }
      writePending(pending);
      writeFavs(applyFavOps(favAccount.list, pending.add, pending.remove));
      if (pending.add.length || pending.remove.length) scheduleFavFlush(0);
    } else if (owner) {
      // Signed out: the account's favourites must not stay visible on this device
      writeFavs([]);
      favSet(FAV_OWNER_KEY, null);
      favSet(FAV_PENDING_KEY, null);
    }
  }

  const syncFavButtons = (popped) => {
    if (!favStore) return;
    const list = readFavs();
    document.querySelectorAll('[data-fav]').forEach((button) => {
      const on = list.includes(button.dataset.fav);
      button.hidden = false;
      button.classList.toggle('is-active', on);
      if (button.hasAttribute('aria-pressed')) button.setAttribute('aria-pressed', on);
      const label = button.querySelector('[data-fav-label]');
      if (label) label.textContent = on ? tr('fav_in') : tr('fav_add');
      if (on && button.dataset.fav === popped) {
        button.classList.remove('is-pop');
        void button.offsetWidth; // restart the animation
        button.classList.add('is-pop');
      }
    });
    document.querySelectorAll('[data-fav-count]').forEach((badge) => {
      badge.hidden = list.length === 0;
      badge.textContent = list.length;
    });
    document.querySelectorAll('[data-fav-link]').forEach((link) => {
      link.setAttribute('aria-label', list.length ? tr('fav_count', { count: list.length }) : tr('favorites'));
    });
  };

  // Quiet confirmation; the live region exists before its first message so screen readers announce it
  let favToast = null;
  let favToastTimer = 0;
  if (favStore) {
    favToast = document.createElement('div');
    favToast.className = 'fav-toast';
    favToast.setAttribute('role', 'status');
    document.body.append(favToast);
  }
  // Keep the toast clear of the Shopify Inbox chat button (it lives in nested shadow roots and the app
  // decides its own offset, e.g. higher above the PDP sticky bar), so measure it instead of guessing.
  let chatActivator = null;
  const findChatActivator = () => {
    if (chatActivator?.isConnected) return chatActivator;
    const walk = (root) => {
      for (const el of root.querySelectorAll('*')) {
        if (el.getAttribute('part') === 'activator') return el;
        if (el.shadowRoot) {
          const found = walk(el.shadowRoot);
          if (found) return found;
        }
      }
      return null;
    };
    chatActivator = walk(document); // only runs when a toast is shown, then cached
    return chatActivator;
  };
  const placeFavToast = () => {
    favToast.style.bottom = '';
    const chat = findChatActivator()?.getBoundingClientRect();
    if (!chat || !chat.width) return;
    const gap = 12;
    const viewport = window.innerHeight;
    const toast = favToast.getBoundingClientRect(); // width/left are final; vertical position comes from "bottom"
    const bottom = parseFloat(getComputedStyle(favToast).bottom) || 0;
    const top = viewport - bottom - toast.height;
    const overlapsX = toast.left < chat.right + gap && toast.right > chat.left - gap;
    const overlapsY = viewport - bottom > chat.top - gap && top < chat.bottom + gap;
    if (overlapsX && overlapsY) favToast.style.bottom = `${Math.ceil(viewport - chat.top + gap)}px`;
  };
  const showFavToast = (message, withLink) => {
    if (!favToast) return;
    const text = document.createElement('span');
    text.textContent = message;
    favToast.replaceChildren(text);
    if (withLink) {
      const link = document.createElement('a');
      link.href = favUrl();
      link.textContent = tr('view');
      favToast.append(link);
    }
    placeFavToast();
    favToast.classList.add('is-visible');
    clearTimeout(favToastTimer);
    favToastTimer = setTimeout(() => favToast.classList.remove('is-visible'), 3800);
  };

  const removeFavItem = (handle) => {
    const item = [...favPage.querySelectorAll('[data-fav-item]')].find((li) => li.dataset.favItem === handle);
    if (!item) return;
    const hadFocus = item.contains(document.activeElement);
    const neighbour = item.nextElementSibling || item.previousElementSibling;
    const finish = () => {
      item.remove();
      const empty = !favPage.querySelector('[data-fav-item]');
      favPage.querySelector('[data-fav-empty]').hidden = !empty;
      if (hadFocus) (neighbour?.querySelector('a') || favPage.querySelector('[data-fav-empty] a'))?.focus();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { finish(); return; }
    item.classList.add('is-leaving');
    setTimeout(finish, 450);
  };

  const toggleFav = (handle) => {
    if (!FAV_HANDLE.test(handle)) return;
    const list = readFavs();
    const on = !list.includes(handle);
    const next = on ? [handle, ...list] : list.filter((h) => h !== handle);
    if (!writeFavs(next)) {
      showFavToast(tr('fav_save_failed'), false);
      return;
    }
    queueFav(on ? [handle] : [], on ? [] : [handle]);
    syncFavButtons(on ? handle : null);
    showFavToast(on ? tr('fav_added') : tr('fav_removed'), on && !favPage);
    if (!on && favPage) removeFavItem(handle);
  };

  // Favoriten page: draw every saved piece with the regular product card (section "favorite-card")
  let favPageRun = 0;
  const renderFavPage = async () => {
    if (!favPage) return;
    const grid = favPage.querySelector('[data-fav-grid]');
    const status = favPage.querySelector('[data-fav-status]');
    const empty = favPage.querySelector('[data-fav-empty]');
    if (!favStore) {
      status.textContent = tr('fav_unavailable');
      status.hidden = false;
      return;
    }
    const run = ++favPageRun;
    const handles = readFavs();
    if (!handles.length) {
      grid.replaceChildren();
      status.hidden = true;
      empty.hidden = false;
      favPage.setAttribute('data-empty', '');
      return;
    }
    favPage.removeAttribute('data-empty');
    empty.hidden = true;
    status.hidden = false;
    status.textContent = tr('fav_loading');
    const results = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(cartUrl(`products/${encodeURIComponent(handle)}?sections=favorite-card`));
        if (response.status === 404) return { handle, gone: true }; // deleted or no longer published
        if (!response.ok) return { handle };
        const html = (await response.json())['favorite-card'] || '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (doc.querySelector('[data-fav-gone]')) return { handle, gone: true }; // deleted: Shopify still answers 200
        const card = doc.querySelector('.card');
        return { handle, card, current: card?.querySelector('[data-fav]')?.dataset.fav };
      } catch (e) {
        return { handle }; // network problem: keep the favourite, try again next visit
      }
    }));
    if (run !== favPageRun) return; // a newer render started meanwhile

    // Drop pieces that no longer exist; follow handles that were renamed (Shopify redirects the old URL)
    const gone = new Set(results.filter((r) => r.gone).map((r) => r.handle));
    const renamed = new Map(results.filter((r) => r.current && r.current !== r.handle).map((r) => [r.handle, r.current]));
    if (gone.size || renamed.size) {
      writeFavs([...new Set(readFavs().filter((h) => !gone.has(h)).map((h) => renamed.get(h) || h))]);
      queueFav([...renamed.values()], [...gone, ...renamed.keys()]);
    }

    const items = results.filter((r) => r.card).map(({ card, current, handle }) => {
      const li = document.createElement('li');
      li.className = 'favorites__item';
      li.dataset.favItem = current || handle;
      li.append(card);
      return li;
    });
    grid.replaceChildren(...items);
    syncFavButtons();
    // Pieces that could not be loaded right now stay saved and simply reappear on the next visit;
    // only when nothing at all could be shown is there a quiet line instead of the grid.
    const failed = results.some((r) => !r.card && !r.gone);
    const unreachable = failed && items.length === 0;
    status.hidden = !unreachable;
    if (unreachable) status.textContent = tr('fav_unreachable');
    empty.hidden = items.length > 0 || unreachable;
    favPage.toggleAttribute('data-empty', !empty.hidden);
  };

  if (favStore) {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-fav]');
      if (!button) return;
      event.preventDefault();
      toggleFav(button.dataset.fav);
    });
    // Another tab changed the list
    window.addEventListener('storage', (event) => {
      if (event.key !== FAV_KEY && event.key !== null) return;
      syncFavButtons();
      renderFavPage();
    });
    // Back/forward cache: the page may show an old state
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      syncFavButtons();
      renderFavPage();
    });
    syncFavButtons();
  }
  renderFavPage();

  // ---------- Product page ----------
  document.querySelectorAll('[data-product]').forEach((root) => {
    const variants = JSON.parse(root.querySelector('[data-variants]')?.textContent || '[]');
    const pickers = [...root.querySelectorAll('[data-option-index]')];
    const idInput = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-add-button]');
    const priceEl = root.querySelector('[data-price]');
    const compareEl = root.querySelector('[data-compare]');
    const compareValueEl = root.querySelector('[data-compare-value]');
    const skuEl = root.querySelector('[data-sku]');
    const stickyBar = root.querySelector('[data-sticky-buy]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const stickyAdd = root.querySelector('[data-sticky-add]');
    const klarna = root.querySelector('klarna-placement');
    const slider = root.querySelector('[data-slider]');
    const counter = root.querySelector('[data-counter]');
    // Always German format ("1.290,00 €"), matching the shop's money format – also on /en,
    // where the page language would otherwise switch live-updated prices to "€1,290.00".
    const money = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: root.dataset.currency || 'EUR',
    });
    const formatCents = (cents) => money.format(cents / 100);

    const setButton = (label, disabled) => {
      [addButton, stickyAdd].forEach((button) => {
        if (!button) return;
        button.textContent = label;
        button.disabled = disabled;
      });
    };

    root.addEventListener('change', (event) => {
      const fieldset = event.target.closest('[data-option-index]');
      if (!fieldset) return;

      fieldset.querySelector('[data-option-value]').textContent = event.target.value;
      const selected = pickers.map((fs) => fs.querySelector('input:checked')?.value);
      const variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));

      if (!variant) {
        setButton(tr('unavailable'), true);
        return;
      }

      idInput.value = variant.id;
      priceEl.textContent = formatCents(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatCents(variant.price);
      if (compareEl && compareValueEl) {
        const hasUvp = variant.compare_at_price > variant.price;
        compareEl.hidden = !hasUvp;
        if (hasUvp) compareValueEl.textContent = formatCents(variant.compare_at_price);
      }
      if (skuEl) skuEl.textContent = variant.sku || variant.barcode || '–';
      setButton(variant.available ? tr('add_to_cart') : tr('sold_out'), !variant.available);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      if (klarna) {
        klarna.dataset.purchaseAmount = variant.price;
        window.Klarna?.OnsiteMessaging?.refresh?.();
      }

      if (variant.featured_media && slider) {
        const target = slider.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (target) slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }
    });

    // Gift option: disabled fields are not submitted, so a normal purchase carries no gift properties
    const gift = root.querySelector('[data-gift]');
    if (gift) {
      const toggle = gift.querySelector('[data-gift-toggle]');
      const panel = gift.querySelector('[data-gift-panel]');
      const recipient = gift.querySelector('[data-gift-recipient]');
      const message = gift.querySelector('[data-gift-message]');
      const count = gift.querySelector('[data-gift-count]');
      const sync = () => {
        const on = toggle.checked;
        const direct = on && gift.querySelector('[data-gift-delivery]:checked')?.value === 'Direkt an Empfänger';
        panel.hidden = !on;
        recipient.hidden = !direct;
        toggle.setAttribute('aria-expanded', on);
        panel.querySelectorAll('input, textarea').forEach((field) => {
          field.disabled = !on || (recipient.contains(field) && !direct);
        });
      };
      gift.addEventListener('change', sync);
      message.addEventListener('input', () => { count.textContent = message.value.length; });
      toggle.disabled = false;
      sync();

      // With the gift option on: express buttons would skip the surcharge, so hide them
      const express = root.querySelector('.pdp__dynamic-checkout');
      gift.addEventListener('change', () => { if (express) express.hidden = toggle.checked; });

      // Add the item and (once per order) the surcharge product in one request, then go to the cart
      const form = addButton?.form;
      const error = gift.querySelector('[data-gift-error]');
      form?.addEventListener('submit', async (event) => {
        if (!toggle.checked) return;
        event.preventDefault();
        error.hidden = true;
        setButton(tr('adding'), true);
        const data = new FormData(form);
        const properties = {};
        data.forEach((value, key) => {
          const name = key.match(/^properties\[(.+)\]$/)?.[1];
          if (name && value) properties[name] = value;
        });
        const feeId = Number(gift.dataset.giftVariant);
        try {
          const cart = await (await fetch(cartUrl('cart.js'))).json();
          const items = [{ id: Number(data.get('id')), quantity: Number(data.get('quantity')) || 1, properties }];
          if (!cart.items.some((item) => item.variant_id === feeId)) items.push({ id: feeId, quantity: 1 });
          const response = await fetch(cartUrl('cart/add.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!response.ok) throw new Error(String(response.status));
          setButton(tr('add_to_cart'), false);
          openCart();
        } catch (e) {
          error.hidden = false;
          setButton(tr('add_to_cart'), false);
        }
      });
    }

    // Add to cart without leaving the page, then show the cart drawer
    const addForm = addButton?.form;
    const addError = root.querySelector('[data-add-error]');
    if (addForm && cartDrawer) {
      addForm.addEventListener('submit', async (event) => {
        if (event.defaultPrevented) return; // gift option handles its own request
        event.preventDefault();
        addError.hidden = true;
        setButton(tr('adding'), true);
        try {
          const response = await fetch(cartUrl('cart/add.js'), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(addForm),
          });
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.description || tr('add_failed'));
          }
          openCart();
        } catch (e) {
          addError.textContent = e.message;
          addError.hidden = false;
        }
        setButton(tr('add_to_cart'), false);
      });
    }

    // Mobile sticky buy bar: visible while the main button is off-screen
    if (stickyBar && addButton && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        stickyBar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(addButton);
    }

    // Mobile gallery counter
    if (slider && counter) {
      slider.addEventListener('scroll', () => {
        counter.textContent = Math.round(slider.scrollLeft / slider.clientWidth) + 1;
      }, { passive: true });
    }
  });

  // ---------- Lightbox for close-up leather / detail shots ----------
  const lightbox = document.querySelector('[data-lightbox]');
  if (lightbox) {
    const stage = lightbox.querySelector('[data-lightbox-stage]');
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zoom]');
      if (!trigger) return;
      const img = document.createElement('img');
      img.className = 'lightbox__img';
      img.src = trigger.dataset.zoom;
      img.alt = trigger.querySelector('img')?.alt || '';
      stage.replaceChildren(img);
      lightbox.showModal();
    });
    lightbox.addEventListener('click', () => lightbox.close());
    lightbox.addEventListener('close', () => stage.replaceChildren());
  }

  // ---------- Product recommendations ----------
  document.querySelectorAll('[data-recommendations]').forEach(async (el) => {
    try {
      const response = await fetch(el.dataset.url);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) {
        el.innerHTML = fresh.innerHTML;
        syncFavButtons(); // the loaded cards bring their own (hidden) hearts
      }
    } catch (e) {
      // Recommendations are optional; fail silently.
    }
  });

  // ---------- Collection filters + sorting: submit on change ----------
  document.querySelectorAll('[data-filter-form]').forEach((form) => {
    form.addEventListener('change', () => {
      const params = new URLSearchParams(new FormData(form));
      [...params.keys()].forEach((key) => { if (!params.get(key)) params.delete(key); });
      window.location.search = params.toString();
    });
  });

  // ---------- Klarna: load only after marketing consent (TDDDG / GDPR) ----------
  const klarnaId = document.body.dataset.klarnaClientId;
  if (klarnaId && document.querySelector('klarna-placement')) {
    let klarnaLoaded = false;
    const loadKlarna = () => {
      if (klarnaLoaded) return;
      klarnaLoaded = true;
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://js.klarna.com/web-sdk/v1/klarna.js';
      script.dataset.clientId = klarnaId;
      document.head.appendChild(script);
    };
    document.addEventListener('visitorConsentCollected', (event) => {
      if (event.detail?.marketingAllowed) loadKlarna();
    });
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error && window.Shopify.customerPrivacy?.marketingAllowed()) loadKlarna();
    });
  }

  // ---------- Cart: exactly one gift surcharge while a gift item is in the cart ----------
  giftFix(document.querySelector('[data-gift-cart]'))
    ?.then((r) => { if (r.ok) window.location.reload(); }).catch(() => {});

  // ---------- Footer: column accordions on mobile, always open on desktop ----------
  const footerAccs = document.querySelectorAll('[data-footer-acc]');
  if (footerAccs.length) {
    const mobile = window.matchMedia('(max-width: 749px)');
    const sync = () => footerAccs.forEach((acc) => {
      acc.open = !mobile.matches;
      acc.querySelector('summary').tabIndex = mobile.matches ? 0 : -1;
    });
    footerAccs.forEach((acc) => acc.addEventListener('toggle', () => {
      if (!mobile.matches && !acc.open) acc.open = true;
    }));
    mobile.addEventListener('change', sync);
    sync();
  }

  // ---------- STERK customer forms: Profil-Dialog + Konto erstellen ----------
  // Both post JSON to the App Proxy (/apps/sterk/…, Cloudflare Worker), which validates again and writes Shopify.
  // Shared fields: snippets/sterk-customer-fields.liquid.
  const sterkForm = (() => {
    const NAME = /^[\p{L}][\p{L}\p{M}' .-]{0,59}$/u;
    const EMAIL = /^[^\s@<>()",;:]{1,64}@[^\s@]+\.[^\s@]{2,}$/;

    const read = (form) => {
      const value = (key) => (form.elements[key] ? (form.elements[key].value || '').replace(/\s+/g, ' ').trim() : '');
      const data = {
        anrede: value('anrede'),
        vorname: value('vorname'),
        nachname: value('nachname'),
        vorwahl: value('vorwahl'),
        telefon: value('telefon'),
        geburtsdatum: value('geburtsdatum'),
        einwilligung: Boolean(form.elements.einwilligung?.checked),
      };
      if (form.elements.email) data.email = value('email').toLowerCase();
      if (form.elements.agb) data.agb = form.elements.agb.checked;
      if (form.elements.firma) data.firma = form.elements.firma.value;
      return data;
    };

    // Same rules as the Worker, so most mistakes are caught without a round trip
    const validate = (data) => {
      const errors = {};
      if ('email' in data) {
        if (!data.email) errors.email = tr('email_missing');
        else if (!EMAIL.test(data.email)) errors.email = tr('email_invalid');
      }
      if (!data.vorname) errors.vorname = tr('first_name_missing');
      else if (!NAME.test(data.vorname)) errors.vorname = tr('first_name_invalid');
      if (!data.nachname) errors.nachname = tr('last_name_missing');
      else if (!NAME.test(data.nachname)) errors.nachname = tr('last_name_invalid');
      if (data.telefon && !/^0*\d{4,14}$/.test(data.telefon.replace(/[\s()./-]/g, ''))) errors.telefon = tr('phone_invalid');
      if (data.geburtsdatum) {
        const m = data.geburtsdatum.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        const date = m && new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
        if (!m || date.getUTCDate() !== +m[1] || date.getUTCMonth() !== +m[2] - 1 || date > new Date()) errors.geburtsdatum = tr('birthday_invalid');
      }
      if ('agb' in data && !data.agb) errors.agb = tr('terms_missing');
      return errors;
    };

    const showErrors = (form, errors) => {
      form.querySelectorAll('[data-error-for]').forEach((p) => {
        const message = errors[p.dataset.errorFor] || '';
        p.textContent = message;
        p.hidden = !message;
        const field = form.elements[p.dataset.errorFor];
        if (field?.setAttribute) field.setAttribute('aria-invalid', message ? 'true' : 'false');
      });
      const first = Object.keys(errors).map((name) => form.elements[name]).find((el) => el?.focus);
      first?.focus();
    };

    // TT-MM-JJJJ: digits only, dashes inserted while typing
    const maskDate = (form) => {
      const input = form.querySelector('[data-date-mask]');
      input?.addEventListener('input', () => {
        const d = input.value.replace(/\D/g, '').slice(0, 8);
        input.value = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('-');
      });
    };

    // Validates, posts, maps the answer; onSuccess(data) on 200 {ok:true}
    const bind = (form, endpoint, onSuccess, extra = () => ({})) => {
      maskDate(form);
      const submit = form.querySelector('[type="submit"]');
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submit.disabled) return;
        const data = { ...read(form), ...extra() };
        const errors = validate(data);
        showErrors(form, errors);
        if (Object.keys(errors).length) return;
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(cartUrl(endpoint), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(data),
            credentials: 'same-origin',
          });
          const result = await response.json().catch(() => ({}));
          if (response.ok && result.ok) { onSuccess(data); return; }
          if (response.status === 422 && result.errors) { showErrors(form, result.errors); return; }
          if (response.status === 401 && endpoint.endsWith('/profil')) { showErrors(form, { form: tr('session_expired') }); return; }
          showErrors(form, { form: tr('failed') });
        } catch (e) {
          showErrors(form, { form: tr('offline') });
        } finally {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        }
      });
    };
    return { bind };
  })();

  // Profil vervollständigen (signed-in customers, sections/profile-dialog.liquid)
  const profileDialog = document.querySelector('[data-profile-dialog]');
  if (profileDialog) {
    const LATER_KEY = 'sterk:profil:spaeter';
    const LATER_DAYS = 14;
    const laterActive = () => {
      try {
        const at = Number(window.localStorage.getItem(LATER_KEY));
        return at > 0 && Date.now() - at < LATER_DAYS * 864e5;
      } catch (e) { return false; }
    };
    const later = () => {
      try { window.localStorage.setItem(LATER_KEY, String(Date.now())); } catch (e) { /* private mode */ }
      profileDialog.close();
    };

    profileDialog.querySelectorAll('[data-profile-later]').forEach((btn) => btn.addEventListener('click', later));
    profileDialog.addEventListener('cancel', (event) => { event.preventDefault(); later(); }); // Esc
    profileDialog.addEventListener('click', (event) => { if (event.target === profileDialog) later(); }); // backdrop
    document.querySelectorAll('[data-profile-open]').forEach((btn) => btn.addEventListener('click', (event) => {
      event.preventDefault();
      if (!profileDialog.open) profileDialog.showModal();
    }));

    if (profileDialog.dataset.autoOpen !== 'false' && !laterActive() && !document.body.classList.contains('template-cart')) {
      window.setTimeout(() => {
        if (!profileDialog.open && !document.querySelector('dialog[open]')) profileDialog.showModal();
      }, 2500);
    }

    sterkForm.bind(profileDialog.querySelector('[data-profile-form]'), 'apps/sterk/profil', () => {
      profileDialog.querySelector('[data-profile-form-wrap]').hidden = true;
      profileDialog.querySelector('[data-profile-done]').hidden = false;
      window.setTimeout(() => profileDialog.close(), 2200);
    });
  }

  // Konto erstellen (sections/main-register.liquid): create the customer, then Shopify's login with the address filled in
  const registerForm = document.querySelector('[data-register-form]');
  if (registerForm) {
    const shownAt = Date.now();
    sterkForm.bind(registerForm, 'apps/sterk/registrieren', (data) => {
      registerForm.hidden = true;
      const done = document.querySelector('[data-register-done]');
      if (done) done.hidden = false;
      const login = new URL(registerForm.dataset.loginUrl);
      login.searchParams.set('login_hint', data.email);
      window.setTimeout(() => { window.location.href = login.toString(); }, 1600);
    }, () => ({ dauer: Date.now() - shownAt }));
  }

  // Account portal step 2: create the customer with every field, then Shopify's login (code) with the address filled in
  const portalStep2 = document.querySelector('[data-portal-register]');
  if (portalStep2) {
    const step1 = portalStep2.parentElement.querySelector('[data-account-form]');
    const email = () => step1.querySelector('input[type="email"]').value.trim().toLowerCase();
    const toLogin = () => {
      step1.dataset.direct = '1';
      step1.requestSubmit(); // step 1's own handler validates the address again, then navigates
    };
    // Changing the address in step 1 updates the hint
    step1.querySelector('input[type="email"]').addEventListener('input', () => {
      portalStep2.querySelector('[data-portal-email]').textContent = email();
    });
    portalStep2.querySelector('[data-portal-skip]').addEventListener('click', toLogin);
    sterkForm.bind(portalStep2, 'apps/sterk/registrieren', toLogin, () => ({
      email: email(),
      dauer: Date.now() - Number(portalStep2.dataset.shownAt || Date.now()),
    }));
  }

  // Marketing opt-in from the registration becomes active with the first sign-in (layout/theme.liquid sets the flag)
  if (document.querySelector('[data-consent-pending]')) {
    fetch(cartUrl('apps/sterk/einwilligung'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: '{}',
      credentials: 'same-origin',
    }).catch(() => { /* next page view retries while the flag exists */ });
  }

  // ---------- Fotos & Dokumente senden (sections/file-send.liquid, Worker src/upload.js) ----------
  const fileDialog = document.querySelector('[data-file-send]');
  if (fileDialog) {
    const form = fileDialog.querySelector('[data-file-send-form]');
    const input = form.querySelector('input[type="file"]');
    const list = form.querySelector('[data-file-list]');
    const submit = form.querySelector('[data-file-send-submit]');
    const progress = form.querySelector('[data-file-progress]');
    const MAX_FILES = 5, MAX_FILE = 10 * 1024 * 1024, MAX_TOTAL = 25 * 1024 * 1024;
    const OK_EXT = /\.(jpe?g|png|webp|heic|heif|pdf)$/i;
    let files = [];
    let openedAt = 0;

    const setError = (key, message) => {
      const p = form.querySelector(`[data-error-for="${key}"]`);
      if (!p) return;
      p.textContent = message || '';
      p.hidden = !message;
      const field = form.elements[key];
      if (field?.setAttribute) field.setAttribute('aria-invalid', message ? 'true' : 'false');
    };
    const size = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
    const render = () => {
      list.replaceChildren(...files.map((file, i) => {
        const li = document.createElement('li');
        const name = document.createElement('span');
        name.className = 'file-send__name';
        name.textContent = file.name;
        const meta = document.createElement('span');
        meta.className = 'file-send__size';
        meta.textContent = size(file.size);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'file-send__remove';
        remove.setAttribute('aria-label', tr('file_remove', { name: file.name }));
        remove.textContent = '×';
        remove.addEventListener('click', () => { files.splice(i, 1); render(); setError('dateien', ''); });
        li.append(name, meta, remove);
        return li;
      }));
    };
    const addFiles = (incoming) => {
      let problem = '';
      for (const file of incoming) {
        if (!OK_EXT.test(file.name)) { problem = tr('file_types'); continue; }
        if (file.size > MAX_FILE) { problem = tr('file_too_big', { name: file.name }); continue; }
        if (files.length >= MAX_FILES) { problem = tr('file_too_many'); break; }
        if (!files.some((f) => f.name === file.name && f.size === file.size)) files.push(file);
      }
      if (!problem && files.reduce((t, f) => t + f.size, 0) > MAX_TOTAL) problem = tr('files_too_big');
      render();
      setError('dateien', problem);
    };
    input.addEventListener('change', () => { addFiles([...input.files]); input.value = ''; });
    const drop = form.querySelector('[data-file-drop]');
    ['dragenter', 'dragover'].forEach((type) => drop.addEventListener(type, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
    ['dragleave', 'drop'].forEach((type) => drop.addEventListener(type, () => drop.classList.remove('is-over')));
    drop.addEventListener('drop', (e) => { e.preventDefault(); addFiles([...(e.dataTransfer?.files || [])]); });

    const open = () => {
      if (fileDialog.open) return;
      document.querySelectorAll('dialog[open]').forEach((d) => { if (d !== fileDialog) d.close?.(); });
      openedAt = Date.now();
      fileDialog.showModal();
    };
    const close = () => fileDialog.close();
    fileDialog.querySelectorAll('[data-file-send-close]').forEach((b) => b.addEventListener('click', close));
    fileDialog.addEventListener('click', (e) => { if (e.target === fileDialog) close(); });
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-file-send-open], a[href$="#dateien-senden"]');
      if (!trigger) return;
      e.preventDefault();
      open();
    });
    if (window.location.hash === '#dateien-senden') open();

    // One file straight to Shopify's storage (staged upload target), with progress
    const uploadOne = (target, file, onProgress) => new Promise((resolve, reject) => {
      const data = new FormData();
      target.parameters.forEach((p) => data.append(p.name, p.value));
      data.append('file', file);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', target.url);
      xhr.upload.addEventListener('progress', (e) => { if (e.lengthComputable) onProgress(e.loaded); });
      xhr.addEventListener('load', () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload ${xhr.status}`))));
      xhr.addEventListener('error', () => reject(new Error('upload network')));
      xhr.send(data);
    });
    const postJson = async (path, body) => {
      const response = await fetch(cartUrl(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        credentials: 'same-origin',
      });
      return { response, result: await response.json().catch(() => ({})) };
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submit.disabled) return;
      const value = (key) => (form.elements[key]?.value || '').trim();
      const fields = { name: value('name'), email: value('email').toLowerCase(), bestellnummer: value('bestellnummer'), nachricht: value('nachricht') };
      ['dateien', 'name', 'email', 'bestellnummer', 'form'].forEach((k) => setError(k, ''));
      const errors = {};
      if (!files.length) errors.dateien = tr('file_missing');
      if (!fields.name) errors.name = tr('name_missing');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(fields.email)) errors.email = tr('email_invalid');
      if (Object.keys(errors).length) {
        Object.entries(errors).forEach(([k, m]) => setError(k, m));
        (form.elements[Object.keys(errors)[0]] || input).focus?.();
        return;
      }

      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      progress.hidden = false;
      progress.textContent = tr('preparing');
      try {
        const start = await postJson('apps/sterk/dateien/start', {
          files: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
          dauer: Date.now() - openedAt,
          firma: form.elements.firma?.value || '',
        });
        if (start.response.status === 422 && start.result.errors) { Object.entries(start.result.errors).forEach(([k, m]) => setError(k, m)); return; }
        if (!start.response.ok || !start.result.ok) throw new Error('start');

        const total = files.reduce((t, f) => t + f.size, 0);
        const done = new Array(files.length).fill(0);
        const show = () => { progress.textContent = tr('uploading', { percent: Math.min(100, Math.round((done.reduce((a, b) => a + b, 0) / total) * 100)) }); };
        await Promise.all(start.result.targets.map((target, i) => uploadOne(target, files[i], (loaded) => { done[i] = loaded; show(); }).then(() => { done[i] = files[i].size; show(); })));

        progress.textContent = tr('sending');
        const send = await postJson('apps/sterk/dateien/senden', {
          ...fields,
          ts: start.result.ts,
          token: start.result.token,
          resourceUrls: start.result.targets.map((t) => t.resourceUrl),
          kinds: start.result.targets.map((t) => t.kind),
        });
        if (send.response.status === 422 && send.result.errors) { Object.entries(send.result.errors).forEach(([k, m]) => setError(k, m)); return; }
        if (!send.response.ok || !send.result.ok) throw new Error('send');

        files = [];
        render();
        form.reset();
        fileDialog.querySelector('[data-file-send-wrap]').hidden = true;
        fileDialog.querySelector('[data-file-send-done]').hidden = false;
      } catch (e) {
        setError('form', tr('upload_failed'));
      } finally {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        progress.hidden = true;
      }
    });
    fileDialog.addEventListener('close', () => {
      if (!fileDialog.querySelector('[data-file-send-done]').hidden) {
        fileDialog.querySelector('[data-file-send-wrap]').hidden = false;
        fileDialog.querySelector('[data-file-send-done]').hidden = true;
      }
    });

    // Hint above the open Shopify Inbox chat window (the chat itself cannot take attachments)
    const chatLink = document.querySelector('[data-file-send-chat-link]');
    if (chatLink) {
      let panel = null, activator = null;
      // Writes only when something changed – the chat reacts to page mutations, so blind writes could loop
      let last = '';
      // v3.42: the link is a top bar glued to the chat panel (same left/width, no gap). When the panel fills the
      // screen height there is no room above, so it turns into a compact pill centred in the panel's own top row.
      // While the chat is open a rAF loop follows the panel every frame, so expand/grow animations never leave it behind.
      const BAR = 40;
      let rafId = 0;
      const place = () => {
        const isOpen = activator?.getAttribute('aria-expanded') === 'true';
        const rect = isOpen && panel ? panel.getBoundingClientRect() : null;
        let state = 'hidden';
        if (rect && rect.width >= 200 && rect.height >= 160) {
          const roomAbove = rect.top - BAR >= 4;
          state = roomAbove
            ? ['bar', Math.round(rect.left), Math.round(rect.width), Math.round(rect.top - BAR)].join(',')
            : ['pill', Math.round(rect.left + rect.width / 2), 0, Math.round(rect.top + 14)].join(',');
        }
        if (isOpen && !rafId) rafId = requestAnimationFrame(() => { rafId = 0; place(); });
        if (state === last) return;
        last = state;
        if (state === 'hidden') { chatLink.hidden = true; return; }
        const [mode, x, width, top] = state.split(',');
        chatLink.style.cssText = mode === 'bar' ? `left:${x}px;width:${width}px;top:${top}px` : `left:${x}px;top:${top}px`;
        chatLink.classList.toggle('is-bar', mode === 'bar');
        chatLink.classList.toggle('is-pill', mode === 'pill');
        chatLink.hidden = false;
      };
      let queued = false;
      const schedule = () => {
        if (queued) return;
        queued = true;
        window.setTimeout(() => { queued = false; scan(); place(); }, 120);
      };
      let scan = () => {};
      // The chat nests its parts in shadow roots (shopify-chat > shopify-agent-runtime); watch every root found
      const hook = (chat) => {
        if (!chat.shadowRoot) return false;
        const watched = new WeakSet();
        const observer = new MutationObserver(schedule);
        scan = () => {
          activator = null; panel = null;
          const walk = (root) => {
            if (!watched.has(root)) { watched.add(root); observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-expanded', 'open'] }); }
            activator = activator || root.querySelector('[part="activator"]');
            panel = panel || root.querySelector('[part="dialog"]');
            root.querySelectorAll('*').forEach((el) => { if (el.shadowRoot) walk(el.shadowRoot); });
          };
          walk(chat.shadowRoot);
        };
        window.addEventListener('resize', schedule, { passive: true });
        // Expand / collapse animates the panel without attribute changes we watch – settle once more afterwards
        document.addEventListener('click', () => window.setTimeout(schedule, 450), { passive: true });
        scan();
        place();
        return true;
      };
      const chat = document.querySelector('shopify-chat');
      if (!(chat && hook(chat))) {
        const watch = new MutationObserver(() => {
          const el = document.querySelector('shopify-chat');
          if (el?.shadowRoot && hook(el)) watch.disconnect();
        });
        watch.observe(document.body, { childList: true, subtree: true });
        window.setTimeout(() => watch.disconnect(), 30000);
      }
    }
  }

  // ---------- Cart: auto-update quantities ----------
  document.querySelectorAll('[data-cart-qty]').forEach((input) => {
    input.addEventListener('change', () => input.form.submit());
  });
})();

// ---------- v3.41: "E-Mail senden" contact form modal (sections/contact-form.liquid) ----------
(() => {
  const dialog = document.querySelector('[data-contact-mail]');
  if (!dialog) return;
  const open = () => {
    if (dialog.open) return;
    document.querySelectorAll('dialog[open]').forEach((d) => { if (d !== dialog) d.close?.(); });
    dialog.showModal();
    const first = dialog.querySelector('input:not([type="hidden"]):not([value]), input[value=""], textarea') || dialog.querySelector('.profile-dialog__title');
    first?.focus({ preventScroll: true });
  };
  const close = () => dialog.close();
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-contact-mail-close]')) { close(); return; }
    const trigger = e.target.closest('[data-contact-mail-open], a[href$="#kontaktformular"]');
    if (!trigger) return;
    e.preventDefault();
    open();
  });
  dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
  if (dialog.hasAttribute('data-open-now') || window.location.hash === '#kontaktformular') open();
})();

// ---------- v3.44: policy pages – lines typed as "- Text" become the same quiet indented lines as real bullets ----------
document.querySelectorAll('.shopify-policy__body .ps_document p').forEach((p) => {
  const first = p.firstChild && p.firstChild.nodeType === 3 ? p.firstChild : (p.firstElementChild?.matches('.bullet:empty') ? p.firstElementChild.nextSibling : null);
  if (first && first.nodeType === 3 && /^\s*[-–]\s+/.test(first.textContent)) {
    first.textContent = first.textContent.replace(/^\s*[-–]\s+/, '');
    p.classList.add('subsection', 'bullets');
  }
});
